import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureError } from '../lib/sentry';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

// Wrapper component to use hooks in ErrorDisplay
const ErrorDisplayWrapper: React.FC<{ error: Error | null; errorInfo: ErrorInfo | null }> = ({ error, errorInfo }) => {
    const navigate = useNavigate();
    const isDev = import.meta.env.DEV;
    
    const handleReload = () => {
        window.location.reload();
    };
    
    const handleGoHome = () => {
        navigate('/');
    };
    
    // DEV mode: Show prominent crash overlay
    if (isDev) {
        return (
            <div className="fixed inset-0 z-[99999] bg-red-600 text-white p-8 overflow-auto">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-red-700 p-6 rounded-lg shadow-2xl border-4 border-red-400">
                        <h1 className="text-4xl font-black mb-4 flex items-center gap-3">
                            <span>💥</span>
                            <span>DEV: App Crashed</span>
                        </h1>
                        <p className="text-xl mb-6 text-red-100">
                            The app encountered a runtime error. Check the details below.
                        </p>
                        
                        <div className="bg-black/50 p-4 rounded-lg mb-4 font-mono text-sm">
                            <div className="text-red-300 font-bold mb-2">Error Message:</div>
                            <div className="text-white mb-4">{error?.message || 'Unknown error'}</div>
                            
                            {error?.stack && (
                                <>
                                    <div className="text-red-300 font-bold mb-2">Stack Trace:</div>
                                    <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-auto max-h-96">
                                        {error.stack}
                                    </pre>
                                </>
                            )}
                        </div>
                        
                        {errorInfo?.componentStack && (
                            <details className="bg-black/30 p-4 rounded-lg mb-4">
                                <summary className="cursor-pointer font-bold text-yellow-300 mb-2">
                                    Component Stack
                                </summary>
                                <pre className="text-xs text-gray-300 whitespace-pre-wrap mt-2">
                                    {errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                        
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleReload}
                                className="px-6 py-3 bg-white text-red-600 rounded-lg font-bold hover:bg-red-50 transition-colors text-lg"
                            >
                                🔄 Reload Page
                            </button>
                            <button
                                onClick={handleGoHome}
                                className="px-6 py-3 bg-red-800 text-white rounded-lg font-bold hover:bg-red-900 transition-colors text-lg"
                            >
                                🏠 Go Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    // PROD mode: User-friendly error UI
    return (
        <div className="min-h-screen flex items-center justify-center bg-red-50 p-10">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-2xl w-full border border-red-100">
                <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
                <p className="text-slate-600 mb-4">The application crashed. Here is the error:</p>
                <pre className="bg-slate-900 text-red-300 p-4 rounded-lg overflow-auto text-sm font-mono mb-4">
                    {error?.toString()}
                </pre>
                <details className="text-xs text-slate-500 font-mono">
                    <summary className="cursor-pointer mb-2 font-bold">Stack Trace</summary>
                    <pre className="whitespace-pre-wrap">
                        {errorInfo?.componentStack}
                    </pre>
                </details>
                <div className="mt-6 flex gap-3">
                    <button
                        onClick={handleGoHome}
                        className="px-6 py-2 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 transition-colors"
                    >
                        Go Home
                    </button>
                    <button
                        onClick={handleReload}
                        className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300 transition-colors"
                    >
                        Reload Page
                    </button>
                </div>
            </div>
        </div>
    );
};

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (import.meta.env.DEV) {
            console.error("Uncaught error:", error, errorInfo);
        }
        this.setState({ error, errorInfo });

        // Send to Sentry
        captureError(error, {
            componentStack: errorInfo.componentStack,
            errorBoundary: true
        });
    }

    public render() {
        if (this.state.hasError) {
            return <ErrorDisplayWrapper error={this.state.error} errorInfo={this.state.errorInfo} />;
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
